/**
 * Created by Jie Gao on 05/24/2021.
 */
import {
    ISymbolSizeConfig,
    ISymbolStateConfig, ISymbolStates,
    StateReelSymbol,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite} from "gsap";
import {GGGameConfig} from "../GGGameConfig";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";

export interface IGGSymbolStates extends ISymbolStates {
    noWin:ISymbolStateConfig;
    attention:ISymbolStateConfig;
    landing:ISymbolStateConfig;
    plus:ISymbolStateConfig;
    explode:ISymbolStateConfig;
}

export class GGReelSymbol extends StateReelSymbol {

    protected _symBackground:PIXI.Sprite;
    protected _symFrame:PIXI.Sprite;
    protected _highlight:PIXI.Sprite;
    protected _displayScale:number;
    protected _symbolAnimations:PIXI.spine.Spine;
    protected _winFrame:PIXI.spine.Spine;
    private _landingAnimation:TimelineSprite;
    private _resizeTimeline:TimelineLite | null;

    public get isM():boolean {
        return this.symName[0] === "M";
    }

    public get symSize():number {
        if(this.symHeight == null) {
            return 3;
        }

        return Math.floor(544 / this.symHeight);
    }

    public set symHeight(value:number) {
        if(value === this.symHeight) {
            return;
        }
        if(this.symName === "X") {
            return;
        }

        const scale:number = (this._symHeight === null)?1:MathHelper.roundToDecimals(value / this._symHeight!, 2);
        this._symHeight = value;
        this.update(value, scale);
    }

    public get symHeight():number {
        if(this._symHeight == null) {
            return GameConfig.instance.SYMBOL_HEIGHT;
        }

        return this._symHeight;
    }

    private update(value:number, scale:number):void {
        const duration:number = (scale===1)?0:0.2;
        this._resizeTimeline = new TimelineLite();
        this._resizeTimeline.add(TweenLite.to(this.scale, duration, {y : scale, transformOrigin : "bottom"}));
        this._resizeTimeline.add(() => {
            this.updateBackground(value);
            this.updateAnimations(value);
        });
    }

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig, size?:ISymbolSizeConfig) {
        super(symName, reelId, state, size);
        if(this.symName.indexOf("X") > -1) {
            this.visible = false;
            return;
        }
    }

    protected initAnimations():void {
        if(!this._symBackground) {
            this._symBackground = this.createEmptySprite("");
            this.addChild(this._symBackground);
        }
        this.createMiddleLayerGraphics();

        if(!this._symFrame) {
            this._symFrame = this.createEmptySprite("");
            this.addChild(this._symFrame);
        }
    }

    //update symbol height and mask
    protected initState(stateConfig:ISymbolStateConfig):boolean {
        if(super.initState(stateConfig)) {
            if(!this.isM) {
                this._stateAnimations[stateConfig.keyword]!.scale.set(this._displayScale, this._displayScale);
            }
            //this.updateAnimations(this.symHeight);
            return true;
        }
        return false;
    }

    protected reset(remove:boolean):void {

        this._currentState = null;
        this._currentStateAnimation = null;

        // TODO: shall we reuse something for the performance?
        if(this._stateAnimations) {
            for(let key in this._stateAnimations) {
                if(this._stateAnimations[key]) {
                    this.removeChild(this._stateAnimations[key]!);
                    this._stateAnimations[key]!.destroy({children : true});
                }
            }
        }
        this._stateAnimations = {};
    }

    protected calDisplayScale(symHeight:number):number {
        return MathHelper.roundToDecimals(symHeight / 181, 2);
    }

    public updateBackground(symHeight:number):void {
        this.scale.set(1,1);
        this._displayScale = this.calDisplayScale(symHeight);
        const isW:boolean = this.symName[0] === "W";
        if(!(this.isM || isW)) {
            this._symBackground.texture = GameResources.getTextures(`${this.symName}_size${this.symSize}_frame`)[0];
        }
    }

    public updateAnimations(symHeight:number):void {
        if(this._symbolAnimations) {
            this._symbolAnimations.scale.x = this._displayScale;
            this._symbolAnimations.scale.y = this._displayScale;
        }
    }

    public onRemove():void {
        super.onRemove();

        if(this._symBackground) {
            this.removeChild(this._symBackground);
        }

        if(this._symbolAnimations) {
            this.removeChild(this._symbolAnimations);
        }

        if(this._landingAnimation) {
            this.removeChild(this._landingAnimation);
        }

        if(this._resizeTimeline) {
            this._resizeTimeline.pause();
            this._resizeTimeline.kill();
            this._resizeTimeline = null;
        }
        if(this._symFrame) {
            this.removeChild(this._symFrame);
        }

        if(this._highlight) {
            this.removeChild(this._highlight);
        }
    }

    //check states
    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);
        if(newState.state.keyword === GGGameConfig.instance.SYMBOL_STATES.explode.keyword) {
            timeline.add(this.getExplodeAnimation(stateAnimation), delay);
        }
    }

    //All state animations
    public playHighlightAnimation():TimelineLite {
        this.createWinFrame();
        return new TimelineLite().add([
            () => {
                this._winFrame.renderable = true;
                this._winFrame.visible = true;
                this._winFrame.state.setAnimation(0, `Size_${this.symSize}`, false);
            }
        ]).add(() => this.hideSymbolAnimation(), 0.83);
    }

    public getExplodeAnimation(stateAnimation:TimelineSprite):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        this.createSymAnimation();
        tl.add([
            () => {
                this._symbolAnimations.state.setAnimation(0, "Explosion", false);
                this._symbolAnimations.renderable = true;
                this._symbolAnimations.visible = true;
            },
            new TweenLite(stateAnimation, 0, {alpha : 0, delay : 0.8}),
            new TweenLite(this._symFrame, 0, {alpha : 0, delay : 0.8}),
            new TweenLite(this._symBackground, 0, {alpha : 0, delay : 0.8})
        ], 0)
            .add(() => this.hideSymbolAnimation(), 1.4);
        return tl;
    }

    public playWinAnimation():TimelineLite {
        this.createWinFrame();
        return new TimelineLite().add([
            () => {
                this._winFrame.renderable = true;
                this._winFrame.visible = true;
                this._winFrame.state.setAnimation(0, `Size_${this.symSize}`, false);
            }
        ]).add(() => this.hideSymbolAnimation(), 1);
    }

    public getIdleAnimation():TimelineLite {
        this.createSymAnimation();
        return new TimelineLite().add(() => {
            this._symbolAnimations.renderable = true;
            this._symbolAnimations.visible = true;
            this._symbolAnimations.state.setAnimation(0, "Idle", false);
        }).add(() => this.hideSymbolAnimation(), 1.3);
    }

    //create and check animations
    public playLandingSmoke():void {
        this._landingAnimation = new TimelineSprite(GameResources.getTextures("landing_L"));
        this._landingAnimation.blendMode = PIXI.BLEND_MODES.ADD;
        this._landingAnimation.name = "landingAnimation";
        this._landingAnimation.anchor.set(0.5, 1);
        this._landingAnimation.position.set(0, this.symHeight*0.5);
        this.addChild(this._landingAnimation)
        this._landingAnimation.getAnimationAutoShowHide(true, true);
    }

    public createMiddleLayerGraphics():void {}

    public createEmptySprite(texture:string, anchorY:number = 0.5):PIXI.Sprite {
        const temp:PIXI.Sprite = new PIXI.Sprite();
        temp.anchor.set(0.5, anchorY);
        temp.position.set(0, 0);
        if(texture.length > 0) {
            temp.texture = GameResources.getTextures(texture)[0];
        }
        return temp;
    }

    protected createSymAnimation():void {
        if(this._symbolAnimations) {
            this._symbolAnimations.destroy();
        }
        this._symbolAnimations = this.createSpineAnimation(this.symName);
        this.addChild(this._symbolAnimations);
        this._symbolAnimations.visible = false;
        this._symbolAnimations.renderable = false;
    }

    protected createWinFrame():void {
        if(this._winFrame) {
            this._winFrame.destroy();
        }
        this._winFrame = this.createSpineAnimation("Highlight");
        this._winFrame.renderable = false;
        this._winFrame.visible = false;
        this.addChild(this._winFrame);
    }

    protected createSpineAnimation(spineName:string, scale:number = -1):PIXI.spine.Spine {
        const spineAnimation = new PIXI.spine.Spine(GameResources.getSpineAsset(spineName));
        const animationScale:number = scale > 0 ? scale : MathHelper.roundToDecimals(this.symHeight / 181, 2);

        spineAnimation.pivot.set(0.5, 0.5);
        spineAnimation.position.set(0, 0);
        spineAnimation.renderable = false;
        spineAnimation.visible = false;
        spineAnimation.name = spineName;
        spineAnimation.scale.y = animationScale;
        spineAnimation.scale.x = animationScale;
        return spineAnimation;
    }

    //public function
    public explode():TweenLite {
        return new TweenLite(this, 0.1, {alpha : 0});
    }

    public hideSymbolAnimation():void {
        if(this._symbolAnimations) {
            this._symbolAnimations.renderable = false;
            this._symbolAnimations.visible = false;
        }
        if(this._winFrame) {
            this._winFrame.renderable = false;
            this._winFrame.visible = false;
        }
    }
}